import { MapLegendItem } from "api/map";

import { replaceLabelCount, stripLabelCount } from "./countWellsInFrame";

export interface ApplyFrameCountsResult {
  /** The rows to render: frame counts where a group matched, live counts elsewhere. */
  readonly legendItems: MapLegendItem[];
  /**
   * How many rows found their group in the counts. Zero across a non-empty
   * legend means the two sides are keyed differently rather than that the frame
   * is empty, which is a bug the silent per-row fallback would otherwise hide.
   */
  readonly matchedRowCount: number;
}

/**
 * Narrows each legend row's count to the capture frame, leaving rows, colors
 * and bin edges alone so nothing re-bins.
 *
 * Rows are matched on the group name with any count prefix stripped: the map's
 * group titles carry one ("(19) Charlie Lake") and a legend item's `title` does
 * not, so matching the raw strings never hits. Matching on the swatch color
 * instead would merge two rows that share a color, and would silently zero a
 * row whose legend color is not byte-identical to the painted one.
 *
 * A row whose group the map does not know keeps its live count — zeroing it
 * would drop it from the screenshot via the caller's count filter, which is a
 * worse answer than the number we do have.
 *
 * The count is carried twice, as `count` and embedded in the label the row
 * renders, so both are rewritten or the label keeps showing the live number.
 */
export const applyFrameCounts = (
  legendItems: MapLegendItem[],
  frameCountsByGroup: Record<string, number>
): ApplyFrameCountsResult => {
  let matchedRowCount = 0;
  const withFrameCounts = legendItems.map((item) => {
    const groupKey = stripLabelCount(item.title) || item.title;
    // Own properties only: a group named like an `Object.prototype` member
    // ("constructor") would otherwise read a function as its count.
    if (!Object.prototype.hasOwnProperty.call(frameCountsByGroup, groupKey)) {
      return item;
    }
    const frameCount = frameCountsByGroup[groupKey];
    matchedRowCount += 1;
    return {
      ...item,
      count: frameCount,
      groupTitle: replaceLabelCount(item.groupTitle, frameCount) ?? item.groupTitle
    };
  });
  return { legendItems: withFrameCounts, matchedRowCount };
};
